import React, { useContext, useEffect, useState } from 'react';
import { SitecoreContext, Field, ImageField, RichText } from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
} from '@mui/material';

import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PreviewStandardImage from '../PreviewStandardImage/PreviewStandardImage';
import { deleteAPICall, getAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import Quantity, { QuantityFieldsProps } from '../Quantity/Quantity';
import { addEllipsis, getCurrencyInUSD, getDateTimePartsFromIso } from '../../utils/helper';
import RichToastNotification, {
  RichToastMesssageProps,
} from '../RichToastNotification/RichToastNotification';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';
import nextId from 'react-id-generator';
import { IFieldSpecificConfig } from '../../model/IFieldSpecificConfig';
import { MODALITY_TITLES } from '../../utils/ProductTypes';
import { removeCartEvent } from '../../utils/analyticsHelper';
import {
  CartItemsDetails,
  CartProductFieldsProps,
} from '../Product/StandardProductCartDetails/StandardProductCartDetails';
import { getIdpToken } from '../../utils/authHelper';
import {
  LOB_DISPLAY_NAMES,
  LOB_TITLES,
  QuantityExceedsCapacity,
  TRAINING_IDENTIFIER,
} from '../../utils/constants';
import { PRODUCT_TYPE_TO_PATH } from '../../model/productConfigHelper';
import { getFieldSpecificConfiguration } from '../../utils/fieldSpecificConfigHelper';

export type TrainingProductCartDetailsProps = {
  fields: {
    ThumbnailImage: ImageField;
    confirmationMessage: Field<string>;
    okButtonText: Field<string>;
    cancelButtonText: Field<string>;
    ToastMessageForRemoveItem: Field<string>;
    CartEmptyText: Field<string>;
    Fields: Array<IFieldSpecificConfig>;
    ThumbnailText: Field<string>;
    GenericFailureMessage: Field<string>;
  };

  quantityFields: QuantityFieldsProps;
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
};

export interface CartDetails {
  quantity: number;
  items: Array<CartItemsDetails>;
}

export const TrainingProductCartDetails = ({
  fields,
}: TrainingProductCartDetailsProps): React.JSX.Element => {
  const [orderID, setOrderID] = useState('');
  const [cartItemDetails, setCartItemDetails] = useState(Array<CartItemsDetails>());
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [showToast, setShowToast] = useState(false);
  const [toastMessages, setToastMessages] = useState<Array<RichToastMesssageProps>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [lineItemId, setLineItemId] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [item, setItem] = useState<CartItemsDetails>();

  const handleOpenDialog = (item: any) => {
    setOpenDialog(true);
    setLineItemId(item.lineItemId);
    setOrderId(item.orderId);
    setItem(item);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  function handleRemoveItem() {
    setOpenDialog(false);
    removeLineItem();
  }

  async function removeLineItem() {
    try {
      await removeAPICall(orderId, lineItemId);
      if (item) {
        removeItem(item);
      }

      quantityUpdated();
      if (dispatch) {
        dispatch({
          type: 'CART_UPDATE',
          payload: {
            toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
            toggleOrderSummaryLoad: !state?.toggleOrderSummaryLoad,
          },
        });
      }
      removeCartEvent(item);
    } catch (error) {
      console.error('Error removing line item:', error);
    }
  }

  const removeAPICall = async (orderId: string, lineItemId: string) => {
    const url = `${process.env.REACT_APP_CART_URL}/CartOrder/${orderId}/lineitems/${lineItemId}`;

    try {
      const response = await deleteAPICall(url);
      if (response && response.status === 200) {
        return response;
      } else {
        throw new Error('Failed to remove item');
      }
    } catch (error) {
      throw error;
    }
  };

  const removeItem = (cartItem: CartItemsDetails) => {
    setCartItemDetails(cartItemDetails.filter((x) => x.lineItemId !== cartItem.lineItemId));

    if (toastMessages) {
      toastMessages.splice(0, toastMessages.length);
    }

    let messages = fields?.ToastMessageForRemoveItem.value.split('##').filter(Boolean);
    messages.map((item) => {
      if (item === 'PRODUCT_TITLE') {
        toastMessages.push({
          type: 'link',
          linkUrl: getProductUrl(cartItem.product),
          message: cartItem.product.title,
        });
      } else {
        toastMessages.push({
          type: 'text',
          message: item,
        });
      }
    });

    setToastMessages([...toastMessages]);
    setShowToast(true);
  };

  const toastRemove = () => {
    setShowToast(false);
  };

  const getFieldSpecificConfig = (productType: string) => {
    let fieldConfigData = getFieldSpecificConfiguration(productType, fields?.Fields);
    const fieldSpecificConfiguration: IFieldSpecificConfig | undefined = fieldConfigData.map(
      (config) => {
        return {
          CartItemMaxQuantity: config.fields.CartItemMaxQuantity,
        };
      }
    )[0];
    return fieldSpecificConfiguration;
  };

  const quantityUpdated = () => {
    fetchData();
    const maxcartItemQuantity = cartItemDetails.reduce(
      (op, item) => (op = op > item.priceOption.quantity ? op : item.priceOption.quantity),
      0
    );

    if (dispatch) {
      dispatch({
        type: 'CART_UPDATE',
        payload: { maxCartItemQuantity: maxcartItemQuantity },
      });
    }
  };
  function getProductCartSkeleton() {
    return (
      <Grid className="productCartGrid" item xs={9} sm={6} md={3}>
        <Card variant="outlined">
          <CardContent>
            <Typography className="title" color="textSecondary" gutterBottom>
              <LoadingSkeleton theme="dark" />
            </Typography>

            <div className="cartDetailGrid">
              <div className="thumbnailsection">
                <LoadingSkeleton theme="light" loaderWidth="100px" loaderHeight="120px" />
              </div>
              <div className="productdetailssection">
                <div className="productInfoSection">
                  <div className="languageSection">
                    <p className="productInfoFieldTitle">Language:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                </div>
              </div>
              <div className="pricedetailssection">
                <p className="priceTitle">Unit Price</p>
                <p className="unitPrice">
                  <LoadingSkeleton theme="light" loaderHeight="30px" />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  function getProductUrl(productDetails: CartProductFieldsProps) {
    const pagePath = PRODUCT_TYPE_TO_PATH[productDetails.modality];

    return `/${pagePath}/${productDetails.productId}`;
  }

  const fetchData = async () => {
    setIsLoading(true);
    let hasSyncFailure = false;

    try {
      const idpToken = getIdpToken();

      const response = await getAPICall(
        `${process.env.REACT_APP_CART_URL}/cartOrder?lob=${TRAINING_IDENTIFIER}`,
        {
          headers: {
            'B2C-Authorization': idpToken,
          },
        }
      );

      if (response && response.data) {
        const data = response.data;
        const items = data.items || [];

        items.forEach((item: any) => {
          if (item.isValid === false) {
            hasSyncFailure = true;
          }
        });

        if (dispatch) {
          const syncFailedCartLOBs = state?.syncFailedCartLOBs || [];

          const updatedSyncFailedCartLOBs = hasSyncFailure
            ? [...new Set([...syncFailedCartLOBs, LOB_DISPLAY_NAMES[TRAINING_IDENTIFIER]])]
            : syncFailedCartLOBs.filter((lob) => lob !== LOB_DISPLAY_NAMES[TRAINING_IDENTIFIER]);

          dispatch({
            type: 'CART_UPDATE',
            payload: {
              syncFailedCartLOBs: updatedSyncFailedCartLOBs,
            },
          });
        }

        setCartItemDetails(items);
        setOrderID(data.orderId || '');
      } else {
        console.error('Empty response from cartOrder API');
      }
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  function renderRichTextMessage(richTextTemplate?: Field<string>, message?: string) {
    if (!richTextTemplate?.value || !message) return null;

    const replacedHtml = richTextTemplate.value.replace('##MESSAGE##', message);
    const sanitizedHtml = DOMPurify.sanitize(replacedHtml);

    return (
      <div className="notificationDetailText" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    );
  }

  function renderRichText(item: CartItemsDetails) {
    let errorMessage = state?.sitecoreDictionaryData?.[item.message];

    if (errorMessage && item.isValid === false) {
      if (item.message === QuantityExceedsCapacity && item?.sessionOptions?.capacityAvailable) {
        errorMessage = errorMessage?.replace(
          '##QUANTITY##',
          item?.sessionOptions?.capacityAvailable.toString() ?? ''
        );
      }
      return renderRichTextMessage(fields?.GenericFailureMessage, errorMessage);
    } else if (item.isValid === false) {
      const genericFailureMessage =
        state?.sitecoreDictionaryData?.GenericErrorMessage.replace(
          '###LOB###',
          LOB_DISPLAY_NAMES[TRAINING_IDENTIFIER]
        ) ||
        `The item is no longer valid. Please remove from the cart and add it from ${LOB_TITLES[TRAINING_IDENTIFIER]} again if needed.`;

      return renderRichTextMessage(fields?.GenericFailureMessage, genericFailureMessage);
    }

    return null;
  }

  function getProductDetailsSection(item: CartItemsDetails) {
    let fieldSpecificConfig = getFieldSpecificConfig(item?.product?.productType);
    return (
      <>
        <Grid container className="productdetailssection" spacing={1} key={nextId()}>
          <Grid item xs={12}>
            <div className="languageSection" key={nextId()}>
              <p className="productInfoFieldTitle" key={nextId()}>
                Language:
              </p>
              <p className="productDetailInnerTitleText" key={nextId()}>
                {item?.product?.language}
              </p>
            </div>
          </Grid>
          {item?.sessionOptions && (
            <Grid item xs={12}>
              <div className="languageSection" key={nextId()}>
                <p className="productInfoFieldTitle" key={nextId()}>
                  Starts:
                </p>
                <p className="productDetailInnerTitleText" key={nextId()}>
                  {getDateTimePartsFromIso(item.sessionOptions.startDate)?.date}{' '}
                  {item.sessionOptions.timezone || ''}
                </p>
              </div>
            </Grid>
          )}
        </Grid>

        <Grid container className="productdetailssection" spacing={1} key={nextId()}>
          <Grid item xs={12}>
            <div className="languageSection" key={nextId()}>
              <p className="productInfoFieldTitle" key={nextId()}>
                Modality:
              </p>
              <p className="productDetailInnerTitleText" key={nextId()}>
                {MODALITY_TITLES[item.product?.modality]}
              </p>
            </div>
          </Grid>

          {item?.sessionOptions && (
            <Grid item xs={12}>
              <div className="languageSection" key={nextId()}>
                <p className="productInfoFieldTitle" key={nextId()}>
                  Ends:
                </p>
                <p className="productDetailInnerTitleText" key={nextId()}>
                  {getDateTimePartsFromIso(item.sessionOptions.endDate)?.date}{' '}
                  {item.sessionOptions.timezone}
                </p>
              </div>
            </Grid>
          )}
        </Grid>

        <div className="pricedetailssection" key={nextId()}>
          <p className="priceTitle" key={nextId()}>
            Unit Price
          </p>
          <p className="unitPrice" key={nextId()}>
            {getCurrencyInUSD(item.priceOption?.unitPrice)}
          </p>
          <Quantity
            key={nextId()}
            fields={{ itemMaxQuantity: fieldSpecificConfig?.CartItemMaxQuantity }}
            quantityFields={{
              orderId: orderID,
              cartItem: item,
            }}
            properties={{
              divId: '',
              removeLinkId: '',
            }}
            quantityUpdated={quantityUpdated}
          ></Quantity>
        </div>
      </>
    );
  }

  return (
    <div>
      <RichToastNotification
        key="richToast"
        toastMessages={toastMessages}
        showToast={showToast}
        removeToast={toastRemove}
      ></RichToastNotification>
      <div className="cartDetailsSection">
        {state?.cartCounts?.lobCounts?.LMS && state?.cartCounts?.lobCounts?.LMS > 0 ? (
          isLoading ? (
            getProductCartSkeleton()
          ) : (
            <Grid container direction="column" justifyContent="space-around" alignItems="stretch">
              {cartItemDetails?.map((item: CartItemsDetails, id: number) => {
                return (
                  <Grid className="productCartGrid" item xs={9} sm={6} md={3} key={nextId()}>
                    <Card variant="outlined" key={nextId()}>
                      <CardContent key={nextId()}>
                        <Typography
                          className="title"
                          color="textSecondary"
                          gutterBottom
                          key={nextId()}
                        >
                          <div className="header-title-area">
                            <Link
                              to={getProductUrl(item.product)}
                              id={`${item.product.productId}${item.product.edition}`}
                              key={nextId()}
                            >
                              {item.product?.title}
                              {addEllipsis(item.product?.title)}
                            </Link>
                            <Link
                              to="#"
                              onClick={() => handleOpenDialog(item)}
                              className="remove-link"
                            >
                              Remove
                            </Link>
                          </div>
                        </Typography>
                        <Dialog open={openDialog} onClose={handleCloseDialog}>
                          <DialogTitle id="alert-dialog-title">
                            {fields.confirmationMessage?.value}
                          </DialogTitle>
                          <DialogActions>
                            <button className="okButton" onClick={handleRemoveItem}>
                              {fields.okButtonText?.value}
                            </button>
                            <button className="cancelButton" onClick={handleCloseDialog} autoFocus>
                              {fields.cancelButtonText?.value}
                            </button>
                          </DialogActions>
                        </Dialog>
                        <div className="cartDetailGrid" key={nextId()}>
                          <div className="thumbnail-main">
                            <div className="thumbnailsection" key={nextId()}>
                              <PreviewStandardImage
                                previewStdFields={{
                                  previewUrl: item.product?.previewUrl,
                                  thumbnailUrl: item.product?.thumbnailUrl,
                                  isDatasheet: item.product?.isDatasheet,
                                }}
                                fields={{
                                  ThumbnailImage: fields.ThumbnailImage,
                                }}
                              ></PreviewStandardImage>
                            </div>
                            <p className="thumbnailTextArea">{fields?.ThumbnailText?.value}</p>
                          </div>
                          {getProductDetailsSection(item)}
                        </div>
                        {item?.sessionOptions?.description && (
                          <div className="descriptionSection">
                            <p className="productInfoFieldTitle">Description:</p>
                            <div
                              className="productDetailInnerTitleText"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(item.sessionOptions.description || ''),
                              }}
                            />
                          </div>
                        )}
                        <div className="errorMessage">{renderRichText(item)}</div>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )
        ) : (
          <div className="empty-cart">
            <RichText field={fields.CartEmptyText} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingProductCartDetails;
